// apps/pwa-pos/src/lib/sync-queue.ts
// Sprint 33 — Reemplaza la dependencia de PowerSync db por localStorage para
// el offline queue. Cuando hay internet, se sube via Edge Functions o RPCs.
//
// Colas soportadas:
//   - ventas: se suben via /functions/v1/pos-upload
//   - cajas:  Sprint 36 — apertura/cierre multi-moneda se suben via RPCs
//             public.abrir_sesion_v2 / cerrar_sesion_arqueo_v2
//   - abonos: registrar_abono via RPC (idempotente por externalId)
//   - notas:  (futuro)

import type { CartLine } from './sales';
import { roundMoney } from '@mibodega/domain';
import type { PagoVenta } from './payments';
import { formatearConsecutivo, reservarConsecutivo } from './consecutivo';
import { getDb } from './db';
import {
  HttpFalloError,
  UploadRechazadoError,
  aplicarFalloAOp,
  clasificarErrorUpload,
  clasificarExcepcion,
  clasificarRpcError,
  esReintentable,
} from './retry-policy';
import { adjustCustomerSaldo } from './customers';
import type {
  MetodoAbono,
  PendingAbono,
  PendingAnular,
  PendingCajaOp,
  PendingVenta,
  VentaHistorialItem,
} from './queue-types';

export type { PendingAbono, PendingAnular, PendingCajaOp, PendingVenta, VentaHistorialItem };

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const HISTORIAL_RETENCION_DIAS = 90;

/** Event the SyncIndicator listens to for immediate online flush. */
export const SYNC_REFRESH_EVENT = 'mibodega:sync-refresh';

/**
 * Ask the UI sync loop to flush queues now (ventas / caja / anular).
 * Safe in Node tests and SSR: no-ops without `window`.
 * Online cashiers must not wait for the 15s poll after a sale.
 */
export function requestSyncFlush(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SYNC_REFRESH_EVENT));
}

export async function getHistorialVentas(): Promise<VentaHistorialItem[]> {
  const db = await getDb();
  const items = await db.getAllFromIndex('historial', 'fecha');
  return items.reverse();
}

async function agregarAlHistorial(item: VentaHistorialItem): Promise<void> {
  const db = await getDb();
  await db.put('historial', item);
}

export async function getUltimaVentaReimprimible(): Promise<VentaHistorialItem | null> {
  const items = await getHistorialVentas();
  for (const v of items) {
    if (v.estado === 'sincronizada' && !v.anulada && v.lineas && v.lineas.length > 0) {
      return v;
    }
  }
  return null;
}

export async function marcarVentaAnuladaEnHistorial(ventaId: string): Promise<void> {
  const db = await getDb();
  const item = await db.get('historial', ventaId);
  if (!item) return;
  await db.put('historial', { ...item, estado: 'anulada', anulada: true });
}

export async function purgarHistorialAntiguo(ahoraMs: number = Date.now()): Promise<number> {
  const corte = new Date(ahoraMs - HISTORIAL_RETENCION_DIAS * 24 * 60 * 60 * 1000).toISOString();
  const db = await getDb();
  const tx = db.transaction('historial', 'readwrite');
  const viejos = await tx.store.index('fecha').getAllKeys(IDBKeyRange.upperBound(corte, true));
  await Promise.all([...viejos.map((key) => tx.store.delete(key)), tx.done]);
  return viejos.length;
}

function loadQueue(): Promise<PendingVenta[]> {
  return getDb().then(async (db) => {
    const items = await db.getAll('ventas_queue');
    return items.map((v) => ({
      ...v,
      intentos: Number.isFinite(v.intentos) ? v.intentos : 0,
      proximoIntentoAt: v.proximoIntentoAt ?? null,
    }));
  });
}

function saveVenta(venta: PendingVenta): Promise<void> {
  return getDb().then(async (db) => {
    await db.put('ventas_queue', venta);
  });
}

/**
 * Elimina items sincronizados de IndexedDB para evitar crecimiento infinito.
 * Se llama automáticamente tras un flush exitoso.
 * Mantiene items con estado 'pendiente' o 'error' para reintento.
 */
export async function purgeSyncedQueue(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('ventas_queue', 'readwrite');
  const sincronizadas = await tx.store.index('estado').getAllKeys('sincronizada');
  await Promise.all([...sincronizadas.map((k) => tx.store.delete(k)), tx.done]);
  if (sincronizadas.length > 0) {
    console.info(`[sync-queue] Limpiados ${sincronizadas.length} items sincronizados`);
  }
}

export async function getPendingCount(): Promise<number> {
  const db = await getDb();
  return db.countFromIndex('ventas_queue', 'estado', 'pendiente');
}

/**
 * Encola una venta localmente. Devuelve el UUID generado y un codigo formateado.
 */
export async function encolarVentaOffline(input: {
  organizationId: string;
  sucursalId: string;
  monedaCodigo: string;
  aplicaIgtf: boolean;
  lineas: CartLine[];
  pagos: PagoVenta[];
  clienteId: string | null;
  esCredito: boolean;
}): Promise<{ ventaId: string; consecutivo: string; total: number }> {
  if (input.lineas.length === 0) {
    throw new Error('La venta debe tener al menos un producto');
  }
  if (input.pagos.length === 0) {
    throw new Error('La venta debe tener al menos un pago');
  }

  const queue = await loadQueue();
  const pisoDesdeCola = queue.reduce((max, v) => {
    const n = Number(v.localConsecutivo);
    return Math.max(max, Number.isFinite(n) ? n : 0);
  }, 0);
  // Se reserva ANTES de escribir la cola: quemar un número si la escritura
  // falla es inocuo; emitir dos veces el mismo número no lo es.
  const nextConsecutivo = reservarConsecutivo(pisoDesdeCola);
  const ventaId = crypto.randomUUID();
  const nowIso = new Date().toISOString();

  const total = roundMoney(input.lineas.reduce((acc, l) => acc + l.subtotal, 0));

  const pendiente: PendingVenta = {
    id: ventaId,
    organizationId: input.organizationId,
    sucursalId: input.sucursalId,
    localConsecutivo: nextConsecutivo,
    fecha: nowIso,
    monedaCodigo: input.monedaCodigo,
    total,
    lineas: input.lineas,
    pagos: input.pagos,
    clienteId: input.clienteId,
    esCredito: input.esCredito,
    estado: 'pendiente',
    intentos: 0,
    proximoIntentoAt: null,
    createdAt: nowIso,
  };

  await saveVenta(pendiente);
  requestSyncFlush();

  return {
    ventaId,
    consecutivo: formatearConsecutivo(nextConsecutivo, nowIso),
    total,
  };
}

/**
 * Sprint 99 POS.5 — forma del resultado por venta que devuelve `pos-upload`.
 * Ver `supabase/functions/pos-upload/_handler.ts`.
 */
interface UploadVentaResult {
  id?: unknown;
  status?: unknown;
  error?: unknown;
  /** Sprint 99 POS.5 — consecutivo oficial que el servidor asignó (formato
   * `V-AAAA-NNNNNN`). Lo propagamos a la venta local para que la UI lo muestre
   * y el historial lo guarde junto al snapshot. */
  numeroFactura?: unknown;
}

/**
 * Decide si `pos-upload` realmente persistió la venta.
 *
 * El handler devuelve HTTP 200 aunque una venta del batch haya explotado: el
 * catch por venta empuja `{ status: 'duplicate', error: <mensaje> }` al array
 * `results`. Por eso la señal de éxito es la AUSENCIA de `error`, no el
 * `status`: un `'duplicate'` limpio es el reenvío idempotente de una venta que
 * ya está en Postgres y cuenta como éxito.
 *
 * Lanza `UploadRechazadoError` con la clasificación ya resuelta cuando la venta
 * no se persistió. Un body ilegible o un resultado ausente también son fallo
 * (transitorio): no hay evidencia de que la venta haya llegado.
 */
async function verificarRespuestaUpload(
  res: Response,
  ventaId: string,
): Promise<{ numeroFactura: string | null }> {
  let cuerpo: unknown;
  try {
    cuerpo = (await res.json()) as unknown;
  } catch (err) {
    throw new UploadRechazadoError(
      `pos-upload devolvio 200 con un cuerpo ilegible: ${err instanceof Error ? err.message : String(err)}`,
      'transitorio',
      { cause: err },
    );
  }

  const results = (cuerpo as { results?: unknown } | null)?.results;
  if (!Array.isArray(results)) {
    throw new UploadRechazadoError(
      'pos-upload devolvio 200 sin el array results: no hay confirmacion de la venta',
      'transitorio',
    );
  }

  const propio = (results as UploadVentaResult[]).find((r) => r?.id === ventaId);
  if (!propio) {
    throw new UploadRechazadoError(
      `pos-upload no devolvio resultado para la venta ${ventaId}`,
      'transitorio',
    );
  }

  if (propio.error !== undefined && propio.error !== null) {
    const mensaje = typeof propio.error === 'string' ? propio.error : String(propio.error);
    throw new UploadRechazadoError(mensaje, clasificarErrorUpload(mensaje));
  }

  if (propio.status !== 'created' && propio.status !== 'duplicate') {
    throw new UploadRechazadoError(
      `pos-upload devolvio un status desconocido para la venta ${ventaId}: ${String(propio.status)}`,
      'transitorio',
    );
  }

  const numeroFacturaRaw = propio.numeroFactura;
  const numeroFactura =
    typeof numeroFacturaRaw === 'string' && numeroFacturaRaw.length > 0 ? numeroFacturaRaw : null;
  return { numeroFactura };
}

/**
 * Intenta subir todas las ventas pendientes via /functions/v1/pos-upload.
 * Devuelve la cantidad subida y la cantidad fallida.
 *
 * Concurrencia: si ya hay un flush en vuelo, la segunda invocacion se
 * serializa detrás de la primera. Asi evitamos dos snapshots simultáneos y
 * que un flush tardío resucite en IDB registros que el primero ya purgó.
 */
let flushEnVuelo: Promise<{ ok: number; fail: number }> | null = null;

export async function flushVentaQueue(accessToken: string): Promise<{ ok: number; fail: number }> {
  if (flushEnVuelo) {
    return flushEnVuelo;
  }
  flushEnVuelo = ejecutarFlushVenta(accessToken).finally(() => {
    flushEnVuelo = null;
  });
  return flushEnVuelo;
}

async function ejecutarFlushVenta(accessToken: string): Promise<{ ok: number; fail: number }> {
  if (!SUPABASE_URL) return { ok: 0, fail: 0 };
  if (!navigator.onLine) return { ok: 0, fail: 0 };

  const queue = await loadQueue();
  const ahora = Date.now();
  const pendientes = queue.filter((v) => esReintentable(v, ahora));
  if (pendientes.length === 0) return { ok: 0, fail: 0 };

  let ok = 0;
  let fail = 0;

  for (const venta of pendientes) {
    try {
      // Backward compat: ventas encoladas con el formato viejo (sin pagos)
      // migran con un pago default EFECTIVO en la moneda de la venta.
      // Sprint 37 R1.
      const pagos =
        venta.pagos && venta.pagos.length > 0
          ? venta.pagos
          : [{ metodo: 'EFECTIVO' as const, monedaCodigo: venta.monedaCodigo, monto: venta.total }];

      // El borde HTTP habla snake_case; `PagoVenta` del cliente es camelCase.
      // Mandar el objeto crudo dejaba `moneda_codigo` en undefined y el INSERT
      // a `pagos_venta` moria contra su NOT NULL, con la venta ya insertada:
      // toda venta offline quedaba en el servidor sin pagos, sin lineas y sin
      // descontar stock. `montoBase` y `tasaCambio` se perdian en silencio por
      // ser nullables, que es peor todavia en pagos en dolares o mixtos.
      const pagosPayload = pagos.map((p) => ({
        metodo: p.metodo,
        moneda_codigo: p.monedaCodigo,
        monto: p.monto,
        monto_base: p.montoBase ?? null,
        tasa_cambio: p.tasaCambio ?? null,
        referencia: p.referencia ?? null,
      }));

      const res = await fetch(`${SUPABASE_URL}/functions/v1/pos-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(SUPABASE_ANON_KEY ? { apikey: SUPABASE_ANON_KEY } : {}),
        },
        body: JSON.stringify({
          ventas: [
            {
              id: venta.id,
              organization_id: venta.organizationId,
              sucursal_id: venta.sucursalId,
              fecha: venta.fecha,
              moneda_codigo: venta.monedaCodigo,
              total: venta.total,
              total_base: venta.total,
              cliente_id: venta.clienteId ?? null,
              es_credito: Boolean(venta.esCredito),
              pagos: pagosPayload,
              lineas: venta.lineas.map((l) => ({
                producto_id: l.productoId,
                presentacion_id: l.presentacionId,
                cantidad: l.cantidad,
                precio_unitario: l.precioUnitario,
                subtotal: l.subtotal,
              })),
            },
          ],
        }),
      });

      if (!res.ok) {
        throw new HttpFalloError(res.status, `HTTP ${res.status}: ${await res.text()}`);
      }

      // Un 200 no alcanza: el body puede traer el fallo de esta venta.
      const { numeroFactura } = await verificarRespuestaUpload(res, venta.id);

      venta.estado = 'sincronizada';
      venta.proximoIntentoAt = null;
      venta.errorMsg = undefined;
      if (numeroFactura) {
        venta.numeroFacturaServidor = numeroFactura;
      }
      // Persistir ANTES de purgar: si la purga falla o un flush concurrente
      // se adelanta, la venta queda en estado terminal y no se reintenta.
      await saveVenta(venta);
      const n = Number(venta.localConsecutivo);
      const codigoFormateado = formatearConsecutivo(Number.isFinite(n) ? n : 1, venta.fecha);
      await agregarAlHistorial({
        id: venta.id,
        organizationId: venta.organizationId,
        sucursalId: venta.sucursalId,
        codigo: codigoFormateado,
        total: venta.total,
        monedaCodigo: venta.monedaCodigo,
        fecha: venta.fecha,
        estado: 'sincronizada',
        syncedAt: new Date().toISOString(),
        lineas: venta.lineas.map((l) => ({
          productoNombre: l.productoNombre,
          presentacionNombre: l.presentacionNombre,
          cantidad: l.cantidad,
          precioUnitario: l.precioUnitario,
          subtotal: l.subtotal,
        })),
        numeroFacturaServidor: numeroFactura ?? undefined,
      });
      ok++;
    } catch (err) {
      aplicarFalloAOp(venta, clasificarExcepcion(err), err);
      fail++;
      // Tambien persistir el fallo: el siguiente flush vera el backoff y no
      // lo reintentara antes de tiempo.
      await saveVenta(venta);
    }
  }

  if (ok > 0) await purgeSyncedQueue();
  return { ok, fail };
}

/**
 * Sprint 99 POS.5 — ventas que necesitan atención del cajero: fallaron y
 * están esperando reintento, o el servidor las rechazó.
 */
export interface VentaConProblema {
  id: string;
  codigo: string;
  total: number;
  monedaCodigo: string;
  fecha: string;
  estado: 'error' | 'rechazada';
  intentos: number;
  errorMsg: string | null;
  proximoIntentoAt: string | null;
}

function esVentaConProblema(v: PendingVenta): boolean {
  return v.estado === 'error' || v.estado === 'rechazada';
}

/**
 * Devuelve el consecutivo local como entero positivo, o null si el valor
 * almacenado no es utilizable (NaN, 0, negativo, faltante). Mantener la salida
 * nula — en vez de caer a 1 — evita que una venta legacy o corrupta aparezca
 * en la UI con la numeración del primer turno del año.
 */
function parseConsecutivo(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

const CODIGO_SIN_CONSECUTIVO = 'V-LEGACY';

function formatearCodigoVenta(venta: Pick<PendingVenta, 'localConsecutivo' | 'fecha'>): string {
  const n = parseConsecutivo(venta.localConsecutivo);
  return n !== null ? formatearConsecutivo(n, venta.fecha) : CODIGO_SIN_CONSECUTIVO;
}

export async function getVentasConProblema(): Promise<VentaConProblema[]> {
  const queue = await loadQueue();
  return queue.filter(esVentaConProblema).map((v) => ({
    id: v.id,
    codigo: formatearCodigoVenta(v),
    total: v.total,
    monedaCodigo: v.monedaCodigo,
    fecha: v.fecha,
    estado: v.estado as 'error' | 'rechazada',
    intentos: v.intentos ?? 0,
    errorMsg: v.errorMsg ?? null,
    proximoIntentoAt: v.proximoIntentoAt ?? null,
  }));
}

export async function getProblemaCount(): Promise<number> {
  const ventas = await loadQueue();
  const abonos = await loadAbonoQueue();
  return ventas.filter(esVentaConProblema).length + abonos.filter(esAbonoConProblema).length;
}

/** Devuelve una venta trabada a 'pendiente' para que el próximo flush la tome. */
export async function reintentarVenta(ventaId: string): Promise<void> {
  const queue = await loadQueue();
  const venta = queue.find((v) => v.id === ventaId);
  if (!venta || !esVentaConProblema(venta)) return;
  venta.estado = 'pendiente';
  venta.proximoIntentoAt = null;
  await saveVenta(venta);
}

export async function reintentarTodas(): Promise<void> {
  const queue = await loadQueue();
  for (const venta of queue) {
    if (esVentaConProblema(venta)) {
      venta.estado = 'pendiente';
      venta.proximoIntentoAt = null;
      await saveVenta(venta);
    }
  }
  await reintentarTodosAbonos();
}

// ============================================================
// Sprint 36 — Cola de sesiones de caja multi-moneda
// ============================================================

import type { SesionCaja, LineaDesglose } from './cash';
import { adoptRemoteSesionAbierta, getSesionCajaActiva, markSesionSincronizada } from './cash';

async function loadCajaQueue(): Promise<PendingCajaOp[]> {
  const db = await getDb();
  const items = await db.getAll('caja_queue');
  return items.map((o) => ({
    ...o,
    intentos: Number.isFinite(o.intentos) ? o.intentos : 0,
    proximoIntentoAt: o.proximoIntentoAt ?? null,
  }));
}

async function saveCajaOp(op: PendingCajaOp): Promise<void> {
  const db = await getDb();
  await db.put('caja_queue', op);
}

export async function getPendingCajaCount(): Promise<number> {
  const db = await getDb();
  return db.countFromIndex('caja_queue', 'estado', 'pendiente');
}

export async function encolarAperturaCaja(input: {
  sesion: SesionCaja;
  organizationId: string;
  sucursalId: string;
  usuarioId: string;
  monedaBase: string;
  desglose: LineaDesglose[];
  idempotencyKey: string;
}): Promise<void> {
  const op: PendingCajaOp = {
    id: crypto.randomUUID(),
    sesionLocalId: input.sesion.id,
    organizationId: input.organizationId,
    sucursalId: input.sucursalId,
    usuarioId: input.usuarioId,
    monedaBase: input.monedaBase,
    tipo: 'APERTURA',
    idempotencyKey: input.idempotencyKey,
    desglose: input.desglose,
    createdAt: new Date().toISOString(),
    estado: 'pendiente',
    intentos: 0,
    proximoIntentoAt: null,
  };
  await saveCajaOp(op);
  requestSyncFlush();
}

export async function encolarCierreCaja(input: {
  sesion: SesionCaja;
  organizationId: string;
  desglose: LineaDesglose[];
  idempotencyKey: string;
}): Promise<void> {
  const op: PendingCajaOp = {
    id: crypto.randomUUID(),
    sesionLocalId: input.sesion.id,
    organizationId: input.organizationId,
    sucursalId: input.sesion.sucursal_id,
    usuarioId: input.sesion.usuario_id,
    monedaBase: input.sesion.moneda_base_codigo,
    tipo: 'CIERRE',
    idempotencyKey: input.idempotencyKey,
    desglose: input.desglose,
    createdAt: new Date().toISOString(),
    estado: 'pendiente',
    intentos: 0,
    proximoIntentoAt: null,
  };
  await saveCajaOp(op);
  requestSyncFlush();
}

interface RpcResultShape {
  data?: unknown;
  error?: { message?: string; code?: string } | null;
}

// Tipo "loose": el cliente Supabase devuelve un PostgrestResponsePromise
// que es thenable. Con `as unknown as Promise<RpcResultShape>` hacemos la promesa.
type RpcCallable = (fn: string, args: Record<string, unknown>) => Promise<RpcResultShape>;

/**
 * Sube pendientes de caja vía RPCs SECURITY DEFINER.
 * Devuelve la cantidad sincronizada y la fallida.
 */
let flushCajaEnVuelo: Promise<{ ok: number; fail: number }> | null = null;

export async function flushCajaQueue(client: {
  rpc: RpcCallable;
  select?: CajaSelectClient;
}): Promise<{ ok: number; fail: number }> {
  if (flushCajaEnVuelo) return flushCajaEnVuelo;
  flushCajaEnVuelo = ejecutarFlushCaja(client).finally(() => {
    flushCajaEnVuelo = null;
  });
  return flushCajaEnVuelo;
}

/** Optional PostgREST reader for recovering a remote open session. */
type CajaQuery = {
  eq: (col: string, val: string) => CajaQuery;
  maybeSingle: () => PromiseLike<{
    data: Record<string, unknown> | null;
    error: { message?: string } | null;
  }>;
};
type CajaSelectClient = {
  from: (table: string) => {
    select: (cols: string) => CajaQuery;
  };
};

function mensajeRpc(err: { message?: string } | null | undefined): string {
  return err?.message ?? '';
}

function esYaTieneSesionAbierta(err: { message?: string } | null | undefined): boolean {
  const m = mensajeRpc(err);
  return m.includes('YA_TIENE_SESION_ABIERTA') || m.includes('sesión de caja ABIERTA');
}

async function ejecutarFlushCaja(client: {
  rpc: RpcCallable;
  /** When present, used to adopt an already-open remote session. */
  select?: CajaSelectClient;
}): Promise<{ ok: number; fail: number }> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { ok: 0, fail: 0 };
  }
  const queue = await loadCajaQueue();
  // Reintentar pendientes Y errores previos que ya vencieron su backoff.
  const ahora = Date.now();
  const pendientes = queue.filter((o) => esReintentable(o, ahora));
  if (pendientes.length === 0) return { ok: 0, fail: 0 };

  let ok = 0;
  let fail = 0;

  for (const op of pendientes) {
    let falloRemoto: unknown;
    try {
      if (op.tipo === 'APERTURA') {
        const payload = {
          sucursalId: op.sucursalId,
          idempotencyKey: op.idempotencyKey,
          desglose: op.desglose.map((d) => ({
            moneda: d.moneda,
            monto: d.monto,
          })),
        };
        const res = await (client.rpc('abrir_sesion_v2', {
          p_payload: payload,
        }) as unknown as Promise<RpcResultShape>);
        if (res.error) {
          // Server already has an open session for this user (local lost the
          // id). Adopt it so the POS can sell and close instead of looping
          // on "Error de sincronización".
          if (esYaTieneSesionAbierta(res.error) && client.select) {
            try {
              const remote = await client.select
                .from('sesiones_caja')
                .select(
                  'id, organization_id, sucursal_id, usuario_id, balance_apertura, moneda_base_codigo, desglose_apertura, fecha_apertura, idempotency_key_apertura, estado',
                )
                .eq('organization_id', op.organizationId)
                .eq('usuario_id', op.usuarioId)
                .eq('estado', 'ABIERTA')
                .maybeSingle();
              const row = remote.data;
              if (!remote.error && row && typeof row.id === 'string') {
                adoptRemoteSesionAbierta({
                  id: String(row.id),
                  organization_id: String(row.organization_id ?? op.organizationId),
                  sucursal_id: String(row.sucursal_id ?? op.sucursalId),
                  usuario_id: String(row.usuario_id ?? op.usuarioId),
                  monto_inicial:
                    row.balance_apertura != null ? Number(row.balance_apertura) : undefined,
                  moneda_base_codigo:
                    row.moneda_base_codigo != null ? String(row.moneda_base_codigo) : op.monedaBase,
                  desglose_apertura: Array.isArray(row.desglose_apertura)
                    ? (row.desglose_apertura as LineaDesglose[])
                    : op.desglose,
                  fecha_apertura:
                    row.fecha_apertura != null ? String(row.fecha_apertura) : undefined,
                  idempotency_key_apertura:
                    row.idempotency_key_apertura != null
                      ? String(row.idempotency_key_apertura)
                      : op.idempotencyKey,
                });
                op.supabaseSesionId = String(row.id);
                op.estado = 'sincronizada';
                op.proximoIntentoAt = null;
                op.errorMsg = undefined;
                await saveCajaOp(op);
                ok++;
                continue;
              }
            } catch (adoptErr) {
              console.warn('[sync-queue] adopt remote sesion failed:', adoptErr);
            }
          }
          falloRemoto = new Error(res.error.message ?? 'RPC abrir_sesion_v2 falló');
          aplicarFalloAOp(op, clasificarRpcError(res.error), falloRemoto);
          fail++;
          await saveCajaOp(op);
          continue;
        }
        op.supabaseSesionId = String(res.data);
      } else {
        // El cierre necesita el ID de sesión que devolvió Supabase al abrir.
        // Si por alguna razón no está en el op de cierre, lo buscamos en la
        // apertura hermana de la misma sesión local.
        let supabaseSesionId = op.supabaseSesionId;
        if (!supabaseSesionId) {
          const apertura = queue.find(
            (o) => o.tipo === 'APERTURA' && o.sesionLocalId === op.sesionLocalId,
          );
          supabaseSesionId = apertura?.supabaseSesionId ?? op.sesionLocalId;
        }
        const payload = {
          sesionId: supabaseSesionId,
          sucursalId: op.sucursalId,
          idempotencyKey: op.idempotencyKey,
          desglose: op.desglose.map((d) => ({
            moneda: d.moneda,
            monto: d.monto,
          })),
        };

        const res = await (client.rpc('cerrar_sesion_arqueo_v2', {
          p_payload: payload,
        }) as unknown as Promise<RpcResultShape>);
        if (res.error) {
          falloRemoto = new Error(res.error.message ?? 'RPC cerrar falló');
          aplicarFalloAOp(op, clasificarRpcError(res.error), falloRemoto);
          fail++;
          await saveCajaOp(op);
          continue;
        }
      }

      op.estado = 'sincronizada';
      op.proximoIntentoAt = null;
      op.errorMsg = undefined;
      await saveCajaOp(op);
      ok++;
    } catch (err) {
      if (!falloRemoto) {
        aplicarFalloAOp(op, clasificarExcepcion(err), err);
        await saveCajaOp(op);
        fail++;
      }
    }
  }

  // Promote local cash session state after a successful flush.
  // CIERRE ops often lack supabaseSesionId (only APERTURA stores the server
  // id). Marking by sesionLocalId alone is enough: the local row is what
  // the UI and abrirSesionCajaOffline read. Without this, PENDIENTE_CIERRE
  // stuck forever and "Abrir caja" threw "Ya existe una sesión…".
  for (const op of queue.filter((o) => o.estado === 'sincronizada')) {
    markSesionSincronizada(op.sesionLocalId, op.tipo === 'APERTURA' ? 'ABIERTA' : 'CERRADA');
  }

  if (ok > 0) await purgeSyncedCajaQueue();
  return { ok, fail };
}

async function purgeSyncedCajaQueue(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('caja_queue', 'readwrite');
  const sincronizadas = await tx.store.index('estado').getAllKeys('sincronizada');
  await Promise.all([...sincronizadas.map((k) => tx.store.delete(k)), tx.done]);
}

// ============================================================
// Sprint 41 — Cola de anulación de ventas
// ============================================================
//
// Cuando el cajero quiere anular una venta ya sincronizada, encola
// esta op. El sync-agent aplica `supabase.rpc('anular_venta', { p_venta_id, p_motivo })`
// la próxima vez que detecte online. Idempotente: si el RPC se llama 2 veces,
// la 2da retorna VENTA_YA_ANULADA.

async function loadAnularQueue(): Promise<PendingAnular[]> {
  const db = await getDb();
  const items = await db.getAll('anular_queue');
  return items.map((o) => ({
    ...o,
    intentos: Number.isFinite(o.intentos) ? o.intentos : 0,
    proximoIntentoAt: o.proximoIntentoAt ?? null,
  }));
}

async function saveAnularOp(op: PendingAnular): Promise<void> {
  const db = await getDb();
  await db.put('anular_queue', op);
}

export async function getPendingAnularCount(): Promise<number> {
  const db = await getDb();
  return db.countFromIndex('anular_queue', 'estado', 'pendiente');
}

let encolarAnularEnVuelo: Promise<unknown> = Promise.resolve();

/**
 * Encola la anulación de una venta. Devuelve `true` si encoló y `false` si ya
 * había una op en vuelo para esa venta — el caller no puede afirmar éxito sin
 * mirar esto.
 *
 * La guarda de idempotencia ignora a propósito las ops en `'rechazada'`
 * (I3 del review final). `clasificarRpcError` manda todo SQLSTATE fuera de la
 * allowlist a permanente, así que cualquier `P0001` de `anular_venta` —sesión
 * de caja cerrada, fuera de la ventana de anulación, token viejo— quedaba
 * terminal. Esos rechazos dependen del estado, no de la validez de la
 * operación: más tarde sí funcionan. Sumado a que esta cola no se purga y a que
 * un rechazo no aparecía en ninguna UI, la venta no se podía anular nunca más.
 * `anular_venta` es idempotente, así que reintentar es seguro.
 */
export async function encolarAnularVenta(input: {
  organizationId: string;
  ventaId: string;
  motivo: string;
}): Promise<boolean> {
  const operacion = encolarAnularEnVuelo.then(async () => {
    const queue = await loadAnularQueue();
    const enVuelo = queue.some(
      (o) => o.ventaId === input.ventaId && o.estado !== 'sincronizada' && o.estado !== 'rechazada',
    );
    if (enVuelo) return false;
    const op: PendingAnular = {
      id: crypto.randomUUID(),
      organizationId: input.organizationId,
      ventaId: input.ventaId,
      motivo: input.motivo.trim(),
      createdAt: new Date().toISOString(),
      estado: 'pendiente',
      intentos: 0,
      proximoIntentoAt: null,
    };
    await saveAnularOp(op);
    requestSyncFlush();
    return true;
  });
  encolarAnularEnVuelo = operacion.catch(() => undefined);
  return operacion;
}

let flushAnularEnVuelo: Promise<{ ok: number; fail: number }> | null = null;

export async function flushAnularQueue(client: {
  rpc: RpcCallable;
}): Promise<{ ok: number; fail: number }> {
  if (flushAnularEnVuelo) return flushAnularEnVuelo;
  flushAnularEnVuelo = ejecutarFlushAnular(client).finally(() => {
    flushAnularEnVuelo = null;
  });
  return flushAnularEnVuelo;
}

async function ejecutarFlushAnular(client: {
  rpc: RpcCallable;
}): Promise<{ ok: number; fail: number }> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { ok: 0, fail: 0 };
  }
  const queue = await loadAnularQueue();
  const ahora = Date.now();
  const pendientes = queue.filter((o) => esReintentable(o, ahora));
  if (pendientes.length === 0) return { ok: 0, fail: 0 };

  let ok = 0;
  let fail = 0;

  for (const op of pendientes) {
    let falloRemoto: unknown;
    try {
      const res = await (client.rpc('anular_venta', {
        p_venta_id: op.ventaId,
        p_motivo: op.motivo,
      }) as unknown as Promise<RpcResultShape>);
      if (res.error) {
        falloRemoto = new Error(res.error.message ?? 'RPC anular_venta falló');
        aplicarFalloAOp(op, clasificarRpcError(res.error), falloRemoto);
        fail++;
        await saveAnularOp(op);
        continue;
      }
      try {
        await marcarVentaAnuladaEnHistorial(op.ventaId);
      } catch (err) {
        // Una falla en la escritura de IDB del historial no debe contar como
        // sincronizacion OK: si se confirmara la op, la UI perderia la op
        // para reintentar y la anulacion quedaria incompleta en historial.
        aplicarFalloAOp(op, clasificarExcepcion(err), err);
        await saveAnularOp(op);
        fail++;
        continue;
      }
      op.estado = 'sincronizada';
      op.proximoIntentoAt = null;
      op.errorMsg = undefined;
      await saveAnularOp(op);
      ok++;
    } catch (err) {
      if (!falloRemoto) {
        aplicarFalloAOp(op, clasificarExcepcion(err), err);
        await saveAnularOp(op);
        fail++;
      }
    }
  }

  return { ok, fail };
}

async function loadAbonoQueue(): Promise<PendingAbono[]> {
  const db = await getDb();
  return db.getAll('abonos_queue');
}

async function saveAbonoOp(op: PendingAbono): Promise<void> {
  const db = await getDb();
  await db.put('abonos_queue', op);
}

export async function getPendingAbonoCount(): Promise<number> {
  const db = await getDb();
  return db.countFromIndex('abonos_queue', 'estado', 'pendiente');
}

function esAbonoConProblema(o: PendingAbono): boolean {
  return o.estado === 'error' || o.estado === 'rechazada';
}

export interface AbonoConProblema {
  id: string;
  clienteNombre: string;
  monto: number;
  monedaCodigo: string;
  metodo: MetodoAbono;
  fecha: string;
  estado: 'error' | 'rechazada';
  errorMsg: string | null;
}

export async function getAbonosConProblema(): Promise<AbonoConProblema[]> {
  const queue = await loadAbonoQueue();
  return queue.filter(esAbonoConProblema).map((o) => ({
    id: o.id,
    clienteNombre: o.clienteNombre,
    monto: o.monto,
    monedaCodigo: o.monedaCodigo,
    metodo: o.metodo,
    fecha: o.createdAt,
    estado: o.estado as 'error' | 'rechazada',
    errorMsg: o.errorMsg ?? null,
  }));
}

export async function reintentarAbono(abonoId: string): Promise<void> {
  const queue = await loadAbonoQueue();
  const op = queue.find((o) => o.id === abonoId);
  if (!op || !esAbonoConProblema(op)) return;
  op.estado = 'pendiente';
  op.proximoIntentoAt = null;
  await saveAbonoOp(op);
}

export async function reintentarTodosAbonos(): Promise<void> {
  const queue = await loadAbonoQueue();
  for (const op of queue) {
    if (esAbonoConProblema(op)) {
      op.estado = 'pendiente';
      op.proximoIntentoAt = null;
      await saveAbonoOp(op);
    }
  }
}

async function resolveSesionCajaRemota(localId?: string): Promise<string | undefined> {
  if (!localId) return undefined;
  const caja = await loadCajaQueue();
  const apertura = caja.find(
    (o) => o.tipo === 'APERTURA' && (o.sesionLocalId === localId || o.supabaseSesionId === localId),
  );
  if (apertura?.supabaseSesionId) return apertura.supabaseSesionId;
  const activa = getSesionCajaActiva();
  if (activa?.id) return activa.id;
  return localId;
}

function esMetodoCaja(metodo: MetodoAbono): boolean {
  return metodo === 'EFECTIVO' || metodo === 'DOLARES';
}

export async function encolarAbono(input: {
  organizationId: string;
  clienteId: string;
  clienteNombre: string;
  monto: number;
  monedaCodigo: string;
  metodo: MetodoAbono;
  referencia?: string;
  sesionCajaId?: string;
  usuarioId: string;
}): Promise<PendingAbono> {
  if (!Number.isFinite(input.monto) || input.monto <= 0) {
    throw new Error('El monto del abono debe ser mayor a 0');
  }
  if (esMetodoCaja(input.metodo) && !input.sesionCajaId) {
    throw new Error('Abrí caja para cobrar un abono en efectivo o dólares');
  }

  const op: PendingAbono = {
    id: crypto.randomUUID(),
    organizationId: input.organizationId,
    clienteId: input.clienteId,
    clienteNombre: input.clienteNombre,
    monto: input.monto,
    monedaCodigo: input.monedaCodigo,
    metodo: input.metodo,
    referencia: input.referencia,
    sesionCajaId: input.sesionCajaId,
    usuarioId: input.usuarioId,
    createdAt: new Date().toISOString(),
    estado: 'pendiente',
    intentos: 0,
    proximoIntentoAt: null,
  };
  await saveAbonoOp(op);
  adjustCustomerSaldo(input.organizationId, input.clienteId, -input.monto);
  requestSyncFlush();
  return op;
}

let flushAbonosEnVuelo: Promise<{ ok: number; fail: number }> | null = null;

export async function flushAbonosQueue(client: {
  rpc: RpcCallable;
}): Promise<{ ok: number; fail: number }> {
  if (flushAbonosEnVuelo) return flushAbonosEnVuelo;
  flushAbonosEnVuelo = ejecutarFlushAbonos(client).finally(() => {
    flushAbonosEnVuelo = null;
  });
  return flushAbonosEnVuelo;
}

function restituirSaldoSiPermanente(op: PendingAbono, tipo: ReturnType<typeof clasificarRpcError>) {
  if (tipo !== 'permanente') return;
  adjustCustomerSaldo(op.organizationId, op.clienteId, op.monto);
}

async function ejecutarFlushAbonos(client: {
  rpc: RpcCallable;
}): Promise<{ ok: number; fail: number }> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { ok: 0, fail: 0 };
  }
  const queue = await loadAbonoQueue();
  const ahora = Date.now();
  const pendientes = queue.filter((o) => esReintentable(o, ahora));
  if (pendientes.length === 0) return { ok: 0, fail: 0 };

  let ok = 0;
  let fail = 0;

  for (const op of pendientes) {
    try {
      const res = await (client.rpc('registrar_abono', {
        p_payload: {
          clienteId: op.clienteId,
          monto: op.monto,
          monedaCodigo: op.monedaCodigo,
          metodo: op.metodo,
          referencia: op.referencia,
          sesionCajaId: await resolveSesionCajaRemota(op.sesionCajaId),
          externalId: op.id,
        },
      }) as unknown as Promise<RpcResultShape>);
      if (res.error) {
        const tipo = clasificarRpcError(res.error);
        aplicarFalloAOp(op, tipo, new Error(res.error.message ?? 'RPC registrar_abono falló'));
        restituirSaldoSiPermanente(op, tipo);
        await saveAbonoOp(op);
        fail++;
        continue;
      }
      op.estado = 'sincronizada';
      op.proximoIntentoAt = null;
      op.errorMsg = undefined;
      await saveAbonoOp(op);
      ok++;
    } catch (err) {
      const tipo = clasificarExcepcion(err);
      aplicarFalloAOp(op, tipo, err);
      restituirSaldoSiPermanente(op, tipo);
      await saveAbonoOp(op);
      fail++;
    }
  }

  return { ok, fail };
}
